import { buildApiUrl, fetchJSON, validateRequiredParams } from './crmApi';

export interface Quote {
  id: string;
  organization_id: string;
  deal_id?: string;
  quote_number: string;
  company_id?: string;
  contact_id?: string;
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  total_cost: number;
  profit_amount: number;
  profit_margin_percent: number;
  valid_until?: string;
  sent_at?: string;
  accepted_at?: string;
  payment_terms?: string;
  delivery_terms?: string;
  notes?: string;
  terms_and_conditions?: string;
  create_supplier_solution: boolean;
  supplier_solution_id?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface QuoteItem {
  id: string;
  quote_id: string;
  product_id?: string;
  supplier_id?: string;
  name: string;
  description?: string;
  quantity: number;
  unit_type: string;
  unit_cost: number;
  unit_price: number;
  line_total: number;
  line_cost: number;
  line_profit: number;
  margin_percent: number;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

export interface CreateQuoteInput {
  organization_id: string;
  deal_id?: string;
  company_id?: string;
  contact_id?: string;
  valid_until?: string;
  payment_terms?: string;
  delivery_terms?: string;
  notes?: string;
  terms_and_conditions?: string;
  create_supplier_solution?: boolean;
  tax_rate?: number;
  discount_amount?: number;
}

export interface UpdateQuoteInput {
  company_id?: string;
  contact_id?: string;
  status?: string;
  valid_until?: string;
  payment_terms?: string;
  delivery_terms?: string;
  notes?: string;
  terms_and_conditions?: string;
  tax_rate?: number;
  discount_amount?: number;
  create_supplier_solution?: boolean;
}

export interface CreateQuoteItemInput {
  quote_id: string;
  product_id?: string;
  supplier_id?: string;
  name: string;
  description?: string;
  quantity: number;
  unit_type?: string;
  unit_cost: number;
  unit_price: number;
  sort_order?: number;
}

export interface UpdateQuoteItemInput {
  name?: string;
  description?: string;
  quantity?: number;
  unit_type?: string;
  unit_cost?: number;
  unit_price?: number;
  supplier_id?: string;
  sort_order?: number;
}

class QuotesService {
  /**
   * Get all quotes for an organization
   */
  async list(organizationId: string, dealId?: string): Promise<{ data: Quote[] }> {
    validateRequiredParams({ organizationId }, ['organizationId'], { service: 'quotesService.list' });
    const params: any = { organization_id: organizationId };
    if (dealId) params.deal_id = dealId;
    const url = buildApiUrl('/api/crm/quotes', params);
    return fetchJSON(url, {
      description: 'List quotes',
      context: { organizationId, dealId }
    });
  }

  /**
   * Get a single quote by ID
   */
  async get(quoteId: string): Promise<{ data: Quote }> {
    validateRequiredParams({ quoteId }, ['quoteId'], { service: 'quotesService.get' });
    const url = buildApiUrl(`/api/crm/quotes/${encodeURIComponent(quoteId)}`);
    return fetchJSON(url, {
      description: 'Get quote',
      context: { quoteId }
    });
  }

  /**
   * Create a new quote
   */
  async create(quote: CreateQuoteInput): Promise<{ data: Quote }> {
    validateRequiredParams(quote, ['organization_id'], { service: 'quotesService.create' });
    const url = buildApiUrl('/api/crm/quotes');
    return fetchJSON(url, {
      method: 'POST',
      body: quote,
      description: 'Create quote',
      context: { organizationId: quote.organization_id }
    });
  }

  /**
   * Update an existing quote
   */
  async update(quoteId: string, updates: UpdateQuoteInput): Promise<{ data: Quote }> {
    validateRequiredParams({ quoteId }, ['quoteId'], { service: 'quotesService.update' });
    const url = buildApiUrl(`/api/crm/quotes/${encodeURIComponent(quoteId)}`);
    return fetchJSON(url, {
      method: 'PATCH',
      body: updates,
      description: 'Update quote',
      context: { quoteId }
    });
  }

  /**
   * Delete a quote
   */
  async delete(quoteId: string): Promise<void> {
    validateRequiredParams({ quoteId }, ['quoteId'], { service: 'quotesService.delete' });
    const url = buildApiUrl(`/api/crm/quotes/${encodeURIComponent(quoteId)}`);
    await fetchJSON(url, {
      method: 'DELETE',
      description: 'Delete quote',
      context: { quoteId }
    });
  }

  /**
   * Send quote to customer
   */
  async send(quoteId: string): Promise<{ data: Quote }> {
    validateRequiredParams({ quoteId }, ['quoteId'], { service: 'quotesService.send' });
    const url = buildApiUrl(`/api/crm/quotes/${encodeURIComponent(quoteId)}/send`);
    return fetchJSON(url, {
      method: 'POST',
      description: 'Send quote',
      context: { quoteId }
    });
  }

  /**
   * Mark quote as accepted
   */
  async accept(quoteId: string): Promise<{ data: Quote }> {
    validateRequiredParams({ quoteId }, ['quoteId'], { service: 'quotesService.accept' });
    const url = buildApiUrl(`/api/crm/quotes/${encodeURIComponent(quoteId)}/accept`);
    return fetchJSON(url, {
      method: 'POST',
      description: 'Accept quote',
      context: { quoteId }
    });
  }

  // ==================== QUOTE ITEMS ====================

  /**
   * Get all items for a quote
   */
  async getItems(quoteId: string): Promise<{ data: QuoteItem[] }> {
    validateRequiredParams({ quoteId }, ['quoteId'], { service: 'quotesService.getItems' });
    const url = buildApiUrl(`/api/crm/quotes/${encodeURIComponent(quoteId)}/items`);
    return fetchJSON(url, {
      description: 'Get quote items',
      context: { quoteId }
    });
  }

  /**
   * Add an item to a quote
   */
  async addItem(item: CreateQuoteItemInput): Promise<{ data: QuoteItem }> {
    validateRequiredParams(item, ['quote_id', 'name', 'quantity', 'unit_cost', 'unit_price'], { service: 'quotesService.addItem' });
    const url = buildApiUrl(`/api/crm/quotes/${encodeURIComponent(item.quote_id)}/items`);
    return fetchJSON(url, {
      method: 'POST',
      body: item,
      description: 'Add quote item',
      context: { quoteId: item.quote_id }
    });
  }

  /**
   * Update a quote item
   */
  async updateItem(itemId: string, updates: UpdateQuoteItemInput): Promise<{ data: QuoteItem }> {
    validateRequiredParams({ itemId }, ['itemId'], { service: 'quotesService.updateItem' });
    const url = buildApiUrl(`/api/crm/quote-items/${encodeURIComponent(itemId)}`);
    return fetchJSON(url, {
      method: 'PATCH',
      body: updates,
      description: 'Update quote item',
      context: { itemId }
    });
  }

  /**
   * Delete a quote item
   */
  async deleteItem(itemId: string): Promise<void> {
    validateRequiredParams({ itemId }, ['itemId'], { service: 'quotesService.deleteItem' });
    const url = buildApiUrl(`/api/crm/quote-items/${encodeURIComponent(itemId)}`);
    await fetchJSON(url, {
      method: 'DELETE',
      description: 'Delete quote item',
      context: { itemId }
    });
  }
}

const quotesService = new QuotesService();
export default quotesService;
